/** Format a number based on a configuration object.
 *	@TODO option keys should be pascalCase, not CamelCase.
 *	@param {Number}		value
 *	@param {Object}		format
 *	@param {Number}		[format.Decimals=0]     Number of decimal places
 *	@param {Number}		[format.Default]        Default number to return when value is falsey
 *	@param {Boolean}	[format.Comma=false]    Add a comma thousands separator
 *	@param {Boolean}	[format.Compact=false]  Use compact magnitudes (K/M/B)
 *	@param {Boolean}	[format.Plus=false]     Display a plus sign in front of positive numbers
 *	@param {String}		[format.Prefix='']      Characters to prepend
 *	@param {String}		[format.Suffix='']      Characters to append
 */
export function formatNumber(v, f) {
	if (! (f.Decimals >= 0)) f.Decimals = 0;
	if (v === 0 || isNaN(v)) return f.Default;
	let o = []; //output
	if (v < 0) {
		o.push('-');
		v = Math.abs(v);
	}
	else if (v > 0 && f.Plus) {
		o.push('+');
	}

	if (f.Prefix !== null) {
		o.push(f.Prefix);
	}

	let ex = ''; // Extension: K/M/B
	if (f.Compact) {
		if (v >= 1000000000) {
			v /= 1000000000;
			ex = 'B';
			f.Decimals = 2;
		}
		else if (v >= 1000000) {
			v /= 1000000;
			ex = 'M';
			f.Decimals = 2;
		}
		// else if (v >= 1000) {
		// 	v /= 1000;
		// 	ex = 'K';
		// 	f.Decimals = 2;
		// }
	}

	v = parseFloat(v).toFixed(f.Decimals);

	if (f.Comma) {
		let end = v.indexOf('.');
		if (end === -1) {
			end = v.length;
		}
		if (end > 3) {
			let parts = [];
			let fraction = v.substr(end,v.length);

			let i = end - 3;
			for (; i > 0; i -= 3) {
				parts.push(v.substr(i,3));
			}
			parts.push(v.substr(0,i+3));
			v = parts.reverse().join(',') + fraction;
		}
	}
	o.push(v);
	o.push(ex);
	if (f.Suffix !== null) {
		o.push(f.Suffix);
	}
	return o.join('');
}


/** Compare a current and previous value, and return a description of the change.
 *	@param {Number} current
 *	@param {Number} previous
 *	@returns {Object} `{ price, percent, class }`
 */
export function calcChange(now, prev) {
	let ret = { price: 0, percent: 0, class: 'unchanged' };
	if (now > 0 && prev > 0) {
		ret.price = now - prev;
		ret.percent = ret.price * 100 / prev;
		if (now > prev) {
			ret.class = 'positive';
		}
		else if (now < prev) {
			ret.class = 'negative';
		}
	}
	return ret;
}


/** Format a timestamp based on age.
 *	@param {Date|String|Number} date	A Date instance or any variant thereof. Timestamps less than 2147483647 are treated as being in seconds.
 *	@TODO this is quite similar to how momentjs or http://npm.im/neatime work, might be better as a dependency.
 */
export function formatLastTradeTime(d) {
	if (! (d instanceof Date)) {
		if (d > 0 && d < 2147483647) {
			d = new Date(d * 1000); // Epoch Seconds
		}
		else {
			d = new Date(d); //Everything else - Epoch Milliseconds, time string, etc.
		}
	}
	if (d.getTimezoneOffset() <= 0) {
		//We are not in 'murica, so defer to broswer
		return d.toLocaleString();
	}
	//We are in 'murica, so we force all times to EST/EDT and then incorrectly label them EST
	let month = d.getMonth();
	let dst = 0;
	if (month > 2 && month < 10) {
		dst = 1;
	}
	else if (month === 2 || month === 10) { // Mar or Nov
		let dstBegin = new Date(d.getYear(),2,7,6,0,0,0); // 7AM GMT == DST switchover time, Mar 7 == earliest possible DST day
		let dow = dstBegin.getDay();
		if (dow > 0) {
			dstBegin.setDate(dstBegin.getDate() + (7 - dow)); // Adjust to 2nd sunday if the 7th isn't a sunday
		}

		let dstEnd = new Date(d.getYear(),10,1,6,0,0,0); // 7AM GMT == DST switchover time, Mar 7 == earliest possible DST day
		dow = dstEnd.getDay();
		if (dow > 0) {
			dstEnd.setDate(dstEnd.getDate() + (7 - dow)); // Adjust to 1st sunday if the 1st isn't a sunday
		}
		dst = (d >= dstBegin && d < dstEnd);
	}

	let tzOffset = dst ? -4 : -5;

	d = new Date(d.getTime() + (tzOffset * 3600000));

	let hour = d.getUTCHours();
	let pm = 0;
	if (hour >= 12) {
		hour -= 12;
		pm = 1;
	}
	if (hour === 0) {
		hour = 12;
	}

	let mins = d.getUTCMinutes();
	if (mins < 10) {
		mins = '0' + mins;
	}
	let secs = d.getUTCSeconds();
	if (secs < 10) {
		secs = '0' + secs;
	}

	let monthAbbr = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];

	let AMorPM = (pm ? ' PM ' : ' AM ');
	if (d.getTime() < new Date().getTime() - (16 * 60 * 60 * 1000)) { //More than 16 hours old, show date
		return monthAbbr + ' ' + d.getDate() + ', ' + d.getFullYear() + ' - ' + hour + ':' + mins + ' ' + AMorPM + ' EST';
	}
	return hour + ':' + mins + ':' + secs + AMorPM + 'EST';
}
